/**
 * Alta y edición de inmueble, en un panel lateral.
 *
 * Para el ALTA son 3 pasos (identificación, características, habitaciones); para la EDICIÓN es un
 * solo panel con todo. En ambos casos, al pie, un desplegable «Documentación» agrupa fotos y
 * papeles del inmueble (titularidad, impuestos, técnicos, seguros, otros) que se suben al guardar. El motivo es que dar de alta obliga a decidir cosas que no se pueden
 * dejar a medias — sobre todo el modo de explotación —, mientras que editar suele ser tocar un
 * campo suelto y un asistente de tres pasos para eso sería un castigo.
 *
 * EL PASO 3 ES EL QUE IMPORTA Y LA UI LO EXPLICA: la ocupación y los KPI salen de las
 * HABITACIONES, no de `properties.bedrooms`. Un piso con 4 dormitorios y 0 habitaciones da 0 % de
 * ocupación por mucho que esté lleno.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { GeocodeCandidate, PropertyDetailResponse } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { Drawer, Stepper } from '../../components/overlay/Drawer';
import {
  Checkbox,
  Field,
  FormRow,
  FormSection,
  Input,
  MoneyInput,
  RadioGroup,
  Select,
  Textarea,
} from '../../components/form/Field';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { PropertyMap } from '../../components/map/LazyPropertyMap';
import { PropertyPhotos } from './PropertyPhotos';
import { PropertyUnits } from './PropertyUnits';
import { useCan } from '../../auth/AuthContext';
import { DEED_GROUPS, DEED_KIND_LABELS, zPostalCode, type DeedKind } from '@naar/shared';
import { toast } from '../../components/feedback/Toaster';
import { formatEur } from '../../lib/format';
import { sumMoney } from '../../lib/moneyInput';
import { COST_SUGGESTIONS } from '../../lib/acquisitionCosts';
import styles from './PropertyFormDrawer.module.css';

const STEPS = ['Identificación', 'Características', 'Habitaciones', 'Documentos'];

/**
 * Un documento en cola, todavía sin subir.
 *
 * En el ALTA la documentación es el último paso, y no un desplegable escondido al pie: el
 * momento en que alguien tiene delante la carpeta del inmueble —escritura, nota simple,
 * certificado energético— es justo este, y después ya no vuelve. Al EDITAR sigue siendo un
 * desplegable cerrado, porque ahí se entra a corregir un dato, no a archivar.
 *
 * Los grupos y subgrupos salen de `DEED_GROUPS`, los mismos que la carpeta del inmueble.
 */
interface PendingDoc {
  kind: DeedKind;
  file: File;
}

const PROPERTY_TYPES = [
  { value: 'piso', label: 'Piso' },
  { value: 'casa', label: 'Casa' },
  { value: 'local', label: 'Local' },
  { value: 'edificio', label: 'Edificio' },
  { value: 'habitaciones', label: 'Alquiler por habitaciones' },
];

const ENERGY_RATINGS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((letter) => ({
  value: letter,
  label: letter,
}));

interface FormState {
  name: string;
  propertyType: string;
  addressLine: string;
  floor: string;
  door: string;
  postalCode: string;
  city: string;
  province: string;
  cadastralReference: string;
  latitude: number | null;
  longitude: number | null;
  geocodePrecision: string;

  areaM2: string;
  bedrooms: string;
  bathrooms: string;
  builtYear: string;
  energyRating: string;
  energyRatingExpiresAt: string;
  acquisitionDate: string;
  acquisitionPrice: string;
  /** Valor de mercado de hoy. Vacío al crear = el precio de compra. */
  marketValue: string;
  /** Costes de compra, línea a línea. El total lo calcula la API al guardar. */
  costItems: CostRow[];
  notes: string;
  /** Enseñar la pestaña «Financiación» en la ficha. */
  financingEnabled: boolean;

  unitMode: 'entera' | 'habitaciones' | 'ninguna';
  unitCount: string;
  /** Renta del inmueble entero, cuando se alquila así. */
  unitRent: string;
  /**
   * Una renta por habitación, en el orden en que se numeran («Hab. 1», «Hab. 2»…). El array
   * lo gobierna `unitCount`: cambiar el número recorta o alarga la lista conservando lo ya
   * escrito, para que bajar de 5 a 4 y volver a 5 no borre las cuatro primeras.
   */
  unitRents: string[];
}

/**
 * Ajusta la lista de rentas al número de habitaciones pedido.
 *
 * Recorta por el final y rellena con vacíos, que es lo que conserva lo ya escrito: bajar de 5 a 4
 * y volver a 5 deja intactas las cuatro primeras en vez de vaciarlas todas.
 */
function ajustarRentas(rentas: string[], cuantas: number): string[] {
  if (cuantas <= rentas.length) return rentas.slice(0, cuantas);
  return [...rentas, ...Array.from({ length: cuantas - rentas.length }, () => '')];
}

/**
 * Un número tal y como se escribe aquí: «85», «85,5», «1.200» o «85 m²».
 *
 * `Number('85,5')` es `NaN`, y `JSON.stringify(NaN)` es `null`: la superficie escrita con
 * coma —que es como se escribe en España— llegaba al servidor como «sin superficie» y la ficha
 * enseñaba «—» sin que nadie supiera por qué. Se admite la coma decimal y se quitan el punto
 * de millar y la unidad; lo que siga sin ser un número se devuelve como `NaN` para que el
 * formulario lo diga, en vez de tragárselo en silencio.
 */
function numeroEs(texto: string): number {
  const limpio = texto.trim().replace(/m²|m2/gi, '').replace(/\s/g, '');
  /*
   * El punto es AMBIGUO: «85.5» es un decimal escrito a la inglesa, pero «1.200» es un millar
   * escrito a la española, y leerlo como 1,2 m² convierte un local de mil metros en un
   * armario. La regla: con coma, todo punto es de millar; sin coma, un punto seguido de
   * exactamente tres cifras al final es de millar, y cualquier otro es decimal.
   */
  const normalizado = limpio.includes(',')
    ? limpio.replace(/\./g, '').replace(',', '.')
    : /^\d{1,3}(\.\d{3})+$/.test(limpio)
      ? limpio.replace(/\./g, '')
      : limpio;
  return normalizado === '' ? Number.NaN : Number(normalizado);
}

/** ¿Lo que se arrastra son ficheros? Arrastrar texto también dispara estos eventos. */
function traeFicheros(event: { dataTransfer: DataTransfer }): boolean {
  return Array.from(event.dataTransfer.types).includes('Files');
}

/** Lo que admite el servidor como documento. Se filtra aquí para decirlo antes de subirlo. */
function esDocumento(file: File): boolean {
  return (
    ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
    /\.(pdf|jpe?g|png|webp)$/i.test(file.name)
  );
}

function recuentoPendiente(fotos: File[], docs: PendingDoc[]): string {
  const total = fotos.length + docs.length;
  return total === 0 ? 'Opcional' : `${total} para subir`;
}

/** Las clases de una fila de documento según lo que tenga y lo que sobrevuele. */
function claseFila(conFichero: boolean, arrastrando: boolean, encima: boolean): string {
  return [
    styles.docRow,
    conFichero ? styles.docRowDone : '',
    arrastrando ? styles.docRowDisponible : '',
    encima ? styles.docRowEncima : '',
  ]
    .filter(Boolean)
    .join(' ');
}

interface CostRow {
  concept: string;
  amount: string;
  /**
   * Confirmada con el «+». Una línea recién añadida está EN BORRADOR —se edita y no suma— hasta
   * que se confirma; así el total no baila mientras tecleas el importe cifra a cifra, y cada
   * coste se añade a conciencia. Las que vienen guardadas del inmueble llegan confirmadas.
   */
  confirmed: boolean;
}

/** Importe que cuenta: algo distinto de cero. */
function tieneImporte(amount: string): boolean {
  return Boolean(amount) && Number(amount) > 0;
}


/**
 * Lo que se envía: fuera las filas sin importe, y una fila con importe pero sin concepto se
 * guarda como «Otros costes» en vez de rechazar el formulario entero por un nombre.
 *
 * Una línea en borrador CON importe también se guarda: si alguien teclea la reforma y pulsa
 * «Guardar» sin pasar por el «+», perder esa cifra en silencio sería peor que guardarla.
 */
function limpiarCostes(rows: CostRow[]): { concept: string; amount: string }[] {
  return rows
    .filter((row) => tieneImporte(row.amount))
    .map((row) => ({ concept: row.concept.trim() || 'Otros costes', amount: importe(row.amount) }));
}

/**
 * Un importe listo para enviar. `MoneyInput` conserva la coma a medio teclear —«500,» es
 * `"500."`— para poder escribir céntimos, y la API rechaza ese punto suelto: quien escribía
 * «500,» y pulsaba «Crear» se quedaba sin alta.
 */
function importe(valor: string): string {
  return valor.replace(/\.$/, '') || '0';
}

/**
 * En qué paso está cada campo que puede rechazar el servidor, y cómo se llama en pantalla.
 *
 * Sin esto, un error de un campo que no está a la vista —el código postal del paso 1 al pulsar
 * «Crear» en el 4— no se veía en ninguna parte: el botón volvía a estar activo y parecía que el
 * alta no hacía nada. Ahora se salta al paso del error y además se dice con un aviso.
 */
const CAMPOS: Record<string, { paso: number; etiqueta: string }> = {
  name: { paso: 0, etiqueta: 'Nombre' },
  propertyType: { paso: 0, etiqueta: 'Tipo' },
  addressLine: { paso: 0, etiqueta: 'Dirección' },
  floor: { paso: 0, etiqueta: 'Planta' },
  door: { paso: 0, etiqueta: 'Puerta' },
  postalCode: { paso: 0, etiqueta: 'Código postal' },
  city: { paso: 0, etiqueta: 'Ciudad' },
  province: { paso: 0, etiqueta: 'Provincia' },
  cadastralReference: { paso: 0, etiqueta: 'Referencia catastral' },
  latitude: { paso: 0, etiqueta: 'Ubicación' },
  longitude: { paso: 0, etiqueta: 'Ubicación' },
  geocodePrecision: { paso: 0, etiqueta: 'Ubicación' },
  areaM2: { paso: 1, etiqueta: 'Superficie' },
  bedrooms: { paso: 1, etiqueta: 'Dormitorios' },
  bathrooms: { paso: 1, etiqueta: 'Baños' },
  builtYear: { paso: 1, etiqueta: 'Año' },
  energyRating: { paso: 1, etiqueta: 'Certificado energético' },
  energyRatingExpiresAt: { paso: 1, etiqueta: 'Caducidad del certificado' },
  acquisitionDate: { paso: 1, etiqueta: 'Fecha de adquisición' },
  acquisitionPrice: { paso: 1, etiqueta: 'Precio' },
  currentMarketValue: { paso: 1, etiqueta: 'Valor de mercado' },
  acquisitionCostItems: { paso: 1, etiqueta: 'Costes de compra' },
  notes: { paso: 1, etiqueta: 'Notas' },
  unitPlan: { paso: 2, etiqueta: 'Habitaciones' },
};

/** `acquisitionCostItems.0.amount` → `acquisitionCostItems`. */
const campoDe = (path: string): string => path.split('.')[0] ?? path;

/** Las claves del formulario que alimentan cada campo de la API, para borrar su error al corregirlo. */
const CLAVES_DE_FORMULARIO: Partial<Record<keyof FormState, string>> = {
  unitRent: 'unitPlan',
  unitRents: 'unitPlan',
  unitMode: 'unitPlan',
  unitCount: 'unitPlan',
  costItems: 'acquisitionCostItems',
};

/** El código postal se comprueba ya en el paso 1, con la misma regla que la API. */
function errorCodigoPostal(valor: string): string | undefined {
  if (!valor.trim()) return undefined;
  const resultado = zPostalCode.safeParse(valor);
  return resultado.success ? undefined : resultado.error.issues[0]?.message;
}

/** El año de construcción, igual: cuatro cifras y no del futuro lejano. */
function errorAnio(valor: string): string | undefined {
  if (!valor.trim()) return undefined;
  const anio = Number(valor);
  if (!Number.isInteger(anio) || anio < 1000) return 'Escribe el año con cuatro cifras: 1975.';
  if (anio > new Date().getFullYear() + 5) return 'Ese año todavía no ha llegado.';
  return undefined;
}

const EMPTY: FormState = {
  name: '',
  propertyType: 'piso',
  addressLine: '',
  floor: '',
  door: '',
  postalCode: '',
  city: '',
  province: '',
  cadastralReference: '',
  latitude: null,
  longitude: null,
  geocodePrecision: 'sin_geocodificar',
  areaM2: '',
  bedrooms: '',
  bathrooms: '',
  builtYear: '',
  energyRating: '',
  energyRatingExpiresAt: '',
  acquisitionDate: '',
  acquisitionPrice: '',
  marketValue: '',
  costItems: [],
  notes: '',
  financingEnabled: false,
  unitMode: 'entera',
  unitCount: '3',
  unitRent: '',
  unitRents: ['', '', ''],
};

function fromProperty(property: PropertyDetailResponse): FormState {
  return {
    ...EMPTY,
    name: property.name,
    propertyType: property.propertyType,
    addressLine: property.addressLine,
    floor: property.floor ?? '',
    door: property.door ?? '',
    postalCode: property.postalCode ?? '',
    city: property.city,
    province: property.province ?? '',
    cadastralReference: property.cadastralReference ?? '',
    latitude: property.latitude,
    longitude: property.longitude,
    geocodePrecision: property.geocodePrecision ?? 'sin_geocodificar',
    areaM2: property.areaM2 === null ? '' : String(property.areaM2),
    bedrooms: property.bedrooms === null ? '' : String(property.bedrooms),
    bathrooms: property.bathrooms === null ? '' : String(property.bathrooms),
    builtYear: property.builtYear === null ? '' : String(property.builtYear),
    energyRating: property.energyRating ?? '',
    energyRatingExpiresAt: property.energyRatingExpiresAt ?? '',
    acquisitionDate: property.acquisitionDate ?? '',
    acquisitionPrice: property.acquisitionPrice,
    /*
     * Solo se rellena si hay una valoración de verdad. Sin ninguna, `valuation.current` es el
     * precio de compra que la API guarda por defecto: mostrarlo aquí hacía creer que el valor
     * estaba puesto y, al guardar sin tocarlo, no se registraba nada (seguía «Sin valorar»).
     */
    marketValue: property.valuation.latest ? property.valuation.current : '',
    costItems:
      property.acquisitionCostItems?.length
        ? property.acquisitionCostItems.map((item) => ({
            concept: item.concept,
            amount: item.amount,
            confirmed: true,
          }))
        : // Un inmueble con total pero sin desglose (no debería quedar ninguno tras la
          // migración, pero por si acaso): el total entra como una línea y no se pierde.
          Number(property.acquisitionCosts) > 0
          ? [{ concept: 'Costes de adquisición', amount: property.acquisitionCosts, confirmed: true }]
          : [],
    notes: property.notes ?? '',
    financingEnabled: property.financingEnabled,
    unitMode: 'ninguna',
  };
}

interface PropertyFormDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Si viene, el panel edita en vez de crear. */
  property?: PropertyDetailResponse;
}

export function PropertyFormDrawer({
  open,
  onClose,
  property,
}: PropertyFormDrawerProps) {
  const editing = Boolean(property);
  const queryClient = useQueryClient();
  const canUnits = useCan('unit:manage');

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeocodeCandidate[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [geocoderDegraded, setGeocoderDegraded] = useState(false);
  const [searching, setSearching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Fotos elegidas mientras se DA DE ALTA, todavía sin subir.
   *
   * Al crear no hay a qué colgarlas: `POST /properties/:id/photos` necesita un inmueble que
   * aún no existe. En vez de esconder el campo hasta después de guardar —que es lo que hacía
   * antes y por lo que «desde crear no salía nada»—, los ficheros se quedan aquí y se suben
   * en cuanto el alta devuelve el identificador, dentro de la misma acción de guardar.
   */
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  /**
   * Documentación en cola. Mismo mecanismo que las fotos y por el mismo motivo, pero esto
   * importa más: el momento en que alguien tiene delante la escritura de un piso es justo
   * cuando lo está dando de alta, con la carpeta abierta. Si hay que crear el inmueble y volver
   * otro día a la pestaña de documentos, la carpeta ya está guardada y los papeles no se suben.
   */
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);
  const docInputRef = useRef<HTMLInputElement>(null);
  const [docTarget, setDocTarget] = useState<DeedKind | null>(null);
  /**
   * Arrastre de ficheros sobre las filas del paso de documentos. `'fotos'` es la fila de fotos;
   * el resto, el tipo de documento. Misma mecánica que la carpeta de la ficha del inmueble:
   * cada fila es una zona de destino y se enciende al entrar un fichero.
   */
  const [sobre, setSobre] = useState<DeedKind | 'fotos' | null>(null);
  const [arrastrando, setArrastrando] = useState(false);

  /* Red de seguridad: si el arrastre acaba fuera del panel no llega `dragleave` y las filas se
     quedarían encendidas y sin puntero. `dragend` y `drop` en la ventana siempre llegan. */
  useEffect(() => {
    function apagar() {
      setArrastrando(false);
      setSobre(null);
    }
    window.addEventListener('dragend', apagar);
    window.addEventListener('drop', apagar);
    return () => {
      window.removeEventListener('dragend', apagar);
      window.removeEventListener('drop', apagar);
    };
  }, []);
  /**
   * La dirección que el usuario YA ELIGIÓ.
   *
   * Sin esto la lista era imposible de cerrar: elegir un resultado escribe su texto en el
   * campo, ese cambio vuelve a disparar el buscador, y 350 ms después la lista reaparecía
   * sola encima del formulario tapando ciudad, provincia y código postal. Mientras el campo
   * siga conteniendo lo elegido no se busca: ya no hay nada que buscar.
   */
  const pickedRef = useRef<string | null>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setFieldErrors({});
    setSuggestions([]);
    setListOpen(false);
    setDirty(false);
    setPendingPhotos([]);
    setPendingDocs([]);
    setUploadProgress(null);
    if (property) {
      setForm(fromProperty(property));
      setAddressQuery(property.addressLine);
      // Editando, la dirección guardada cuenta como ya elegida: abrir el panel para cambiar
      // el nombre no tiene por qué lanzar una búsqueda ni desplegar nada.
      pickedRef.current = property.addressLine;
    } else {
      setForm(EMPTY);
      setAddressQuery('');
      pickedRef.current = null;
    }
    /*
     * Depende del IDENTIFICADOR del inmueble, no del objeto. Editando, el paso «Habitaciones» y
     * las fotos del paso 4 guardan al momento y refrescan el inmueble; con el objeto como
     * dependencia, ese refresco reiniciaba el formulario: volvías al paso 1 y perdías lo que
     * llevaras escrito en los otros pasos.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, property?.id]);

  /** Importe de cada línea de coste, para llevar el cursor a la que se acaba de añadir. */
  const costAmountRefs = useRef<(HTMLInputElement | null)[]>([]);

  /**
   * Añade una línea de coste. Con concepto (un atajo), el cursor va al importe, que es lo
   * único que falta; sin él («Otro coste»), al concepto.
   */
  function addCost(concept: string) {
    const index = form.costItems.length;
    patch({ costItems: [...form.costItems, { concept, amount: '', confirmed: false }] });
    requestAnimationFrame(() => {
      const amount = costAmountRefs.current[index];
      if (!amount) return;
      if (concept) amount.focus();
      else (amount.closest('div')?.parentElement?.querySelector('input') as HTMLInputElement | null)?.focus();
    });
  }

  /** El «+»: la línea pasa a contar en el total. Sin importe no hay nada que confirmar. */
  function confirmCost(index: number) {
    const row = form.costItems[index];
    if (!row || !tieneImporte(row.amount)) return;
    patch({
      costItems: form.costItems.map((other, i) =>
        i === index ? { ...other, concept: other.concept.trim() || 'Otros costes', confirmed: true } : other,
      ),
    });
  }

  /** Volver a abrir una línea confirmada para corregirla. Mientras se edita, no suma. */
  function editCost(index: number) {
    patch({
      costItems: form.costItems.map((other, i) => (i === index ? { ...other, confirmed: false } : other)),
    });
    requestAnimationFrame(() => costAmountRefs.current[index]?.focus());
  }

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
    setDirty(true);
    // Corregir un campo quita su error: si no, seguiría en rojo hasta el siguiente guardado.
    const tocados = new Set(
      (Object.keys(next) as (keyof FormState)[]).map((clave) => CLAVES_DE_FORMULARIO[clave] ?? clave),
    );
    setFieldErrors((current) => {
      const quedan = Object.entries(current).filter(([path]) => !tocados.has(campoDe(path)));
      return quedan.length === Object.keys(current).length ? current : Object.fromEntries(quedan);
    });
  }

  // -------------------------------------------------------------------------
  // Geocodificación
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return undefined;
    const query = addressQuery.trim();
    // Mínimo 4 caracteres: por debajo no identifica nada y solo gasta cuota de las fuentes.
    if (query.length < 4 || query === pickedRef.current) {
      setSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      /**
       * `AbortController` en la limpieza: cancela la petición obsoleta. Sin esto, una respuesta
       * lenta de hace tres letras puede aterrizar DESPUÉS de la nueva y pisar la lista con
       * resultados de una búsqueda que el usuario ya no está haciendo.
       */
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setSearching(true);
      try {
        const result = await api.geocode(query);
        if (controller.signal.aborted) return;
        setSuggestions(result.candidates);
        setListOpen(result.candidates.length > 0);
        setGeocoderDegraded(result.degraded);
      } catch {
        setGeocoderDegraded(true);
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [addressQuery, open]);

  /**
   * Un clic fuera cierra la lista. Es la otra mitad del arreglo: aunque elegir ya la cierra,
   * quien abre el desplegable y decide que ninguna opción le sirve necesita poder salir de él
   * sin escribir nada ni elegir una dirección que no es la suya.
   */
  useEffect(() => {
    if (!listOpen) return undefined;

    function onPointerDown(event: PointerEvent) {
      if (!autocompleteRef.current?.contains(event.target as Node)) setListOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [listOpen]);

  async function pickSuggestion(candidate: GeocodeCandidate) {
    patch({
      addressLine: candidate.addressLine ?? candidate.label,
      city: candidate.city ?? form.city,
      province: candidate.province ?? form.province,
      postalCode: candidate.postalCode ?? form.postalCode,
      latitude: candidate.latitude,
      longitude: candidate.longitude,
      geocodePrecision: candidate.precision,
      /*
       * La REFERENCIA CATASTRAL no se rellena sola, aunque la fuente la traiga. Identifica un
       * inmueble concreto —planta y puerta incluidas— y el geocodificador acierta la finca,
       * no la vivienda: colar la del portal daría un dato que parece verificado, no lo está, y
       * nadie vuelve a mirar un campo que ya viene lleno. La escribe quien tiene la escritura
       * delante.
       */
    });
    const picked = candidate.addressLine ?? candidate.label;
    setAddressQuery(picked);
    pickedRef.current = picked;
    setSuggestions([]);
    setListOpen(false);

    /**
     * Si la fuente trajo referencia catastral se consulta el Catastro para ofrecer superficie y
     * año, los dos campos que más se odia teclear. La referencia se usa solo para la consulta:
     * no se escribe en el formulario. Es una SUGERENCIA: solo rellena lo que está vacío, nunca
     * sobrescribe lo que el usuario ya haya puesto, y lo dice con un aviso para que se revise.
     */
    if (candidate.cadastralReference) {
      try {
        const info = await api.cadastral(candidate.cadastralReference);
        if (!info) return;
        setForm((current) => ({
          ...current,
          areaM2: current.areaM2 || (info.areaM2 ? String(info.areaM2) : ''),
          builtYear: current.builtYear || (info.builtYear ? String(info.builtYear) : ''),
        }));
        if (info.areaM2 || info.builtYear) {
          toast.info('Superficie y año rellenados desde el Catastro. Revísalos.');
        }
      } catch {
        // El Catastro es opcional: su fallo no debe interrumpir el alta.
      }
    }
  }

  function movePin(latitude: number, longitude: number) {
    // Arrastrar el pin es una decisión explícita: la precisión pasa a `manual`, que es lo
    // honesto. Sin esto, la UI seguiría diciendo «ubicación exacta» sobre un pin movido a ojo.
    patch({ latitude, longitude, geocodePrecision: 'manual' });
  }

  // -------------------------------------------------------------------------
  // Envío
  // -------------------------------------------------------------------------

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        propertyType: form.propertyType,
        addressLine: form.addressLine.trim(),
        floor: form.floor.trim() || null,
        door: form.door.trim() || null,
        city: form.city.trim(),
        province: form.province.trim() || null,
        postalCode: form.postalCode.trim() || null,
        latitude: form.latitude,
        longitude: form.longitude,
        geocodePrecision: form.latitude === null ? undefined : form.geocodePrecision,
        cadastralReference: form.cadastralReference.trim() || null,
        areaM2: form.areaM2 ? numeroEs(form.areaM2) : null,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        builtYear: form.builtYear ? Number(form.builtYear) : null,
        energyRating: form.energyRating || null,
        energyRatingExpiresAt: form.energyRatingExpiresAt || null,
        acquisitionDate: form.acquisitionDate || null,
        acquisitionPrice: importe(form.acquisitionPrice),
        acquisitionCostItems: limpiarCostes(form.costItems),
        notes: form.notes.trim() || null,
        financingEnabled: form.financingEnabled,
      };

      /*
       * Valor de mercado. Al crear, si se deja vacío la API toma el precio de compra. Al
       * editar solo se manda si ha CAMBIADO: cada cambio queda como una valoración en el
       * histórico, y reenviar el mismo valor en cada guardado lo llenaría de duplicados.
       */
      if (form.marketValue) {
        const valor = importe(form.marketValue);
        if (
          !editing ||
          !property?.valuation.latest ||
          Number(valor) !== Number(property.valuation.current)
        ) {
          body.currentMarketValue = valor;
        }
      }

      if (!editing && form.unitMode !== 'ninguna') {
        body.unitPlan =
          form.unitMode === 'entera'
            ? { mode: 'entera', baseRent: importe(form.unitRent) }
            : { mode: 'habitaciones', rents: form.unitRents.map(importe) };
      }

      const saved = editing
        ? await api.updateProperty(property!.id, body, property!.updatedAt)
        : await api.createProperty(body);
      const propertyId = editing ? property!.id : saved.id;

      /**
       * Las fotos van DESPUÉS y de una en una, ya con el identificador en la mano. Si alguna
       * falla no se deshace el alta: el inmueble ya existe y es lo que costó rellenar; se avisa
       * y se pueden reintentar desde la ficha. Perder el formulario entero por una foto de 20
       * MB sería un intercambio pésimo.
       */
      for (const [index, file] of pendingPhotos.entries()) {
        try {
          await api.uploadPhoto(propertyId, file, (percent) => {
            setUploadProgress(Math.round(((index + percent / 100) / pendingPhotos.length) * 100));
          });
        } catch {
          toast.warning(`«${file.name}» no se ha podido subir. Puedes añadirla desde la ficha.`);
        }
      }

      /*
       * Y la documentación, igual. Se cuenta aparte de las fotos en la barra de progreso porque
       * un PDF de 20 MB tarda lo suyo y mezclarlo con las miniaturas daba un porcentaje que
       * saltaba de 30 a 90 sin explicación.
       */
      for (const [index, doc] of pendingDocs.entries()) {
        try {
          await api.uploadDeed(propertyId, doc.file, { kind: doc.kind }, (percent) => {
            setUploadProgress(Math.round(((index + percent / 100) / pendingDocs.length) * 100));
          });
        } catch {
          toast.warning(
            `«${doc.file.name}» (${DEED_KIND_LABELS[doc.kind]}) no se ha podido subir. Puedes añadirlo desde la ficha.`,
          );
        }
      }

      setUploadProgress(null);
      return saved;
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      if (editing) void queryClient.invalidateQueries({ queryKey: queryKeys.property(property!.id) });
      toast.success(editing ? 'Inmueble actualizado.' : `«${saved.name}» dado de alta.`);
      setDirty(false);
      onClose();
    },
    onError: (error: unknown) => {
      if (!(error instanceof ApiError)) {
        toast.error('No se ha podido guardar.');
        return;
      }

      /**
       * Los errores de servidor se pintan EN EL CAMPO, no en un banner arriba. Un 409 de
       * referencia catastral duplicada tiene que aparecer junto a la referencia catastral,
       * que es donde el usuario puede arreglarlo.
       */
      const errors: Record<string, string> = {};
      for (const detail of error.fieldErrors) errors[detail.path] = detail.message;

      if (error.code === 'DUPLICATE_CADASTRAL_REF') {
        const payload = error.details as { existingPropertyName?: string } | undefined;
        errors.cadastralReference = `Ya la tiene «${payload?.existingPropertyName ?? 'otro inmueble'}».`;
        setStep(0);
      }
      if (error.code === 'STALE_RESOURCE') {
        toast.error('Alguien lo ha modificado mientras editabas. Recarga para ver los cambios.');
      }

      setFieldErrors(errors);
      const paths = Object.keys(errors);
      if (paths.length === 0) {
        if (error.code !== 'STALE_RESOURCE') toast.error(error.message);
        return;
      }

      /*
       * Se vuelve al primer paso con un error: dejar al usuario en el paso 4 con un error
       * invisible tres pantallas atrás es la peor forma de comunicarlo. Y se dice además con un
       * aviso, porque el campo puede quedar fuera de la vista y un botón que «no hace nada» es
       * justo lo que hacía creer que el alta no funcionaba.
       */
      const pasos = paths.map((path) => CAMPOS[campoDe(path)]?.paso ?? step);
      setStep(Math.min(...pasos));
      const primero = paths[0]!;
      const etiqueta = CAMPOS[campoDe(primero)]?.etiqueta;
      const detalle = etiqueta ? `${etiqueta}: ${errors[primero]}` : errors[primero];
      toast.error(
        paths.length === 1 ? `No se ha guardado. ${detalle}` : `No se ha guardado: hay ${paths.length} datos que corregir. ${detalle}`,
      );
    },
  });

  const stepValid = useMemo(() => {
    if (step === 0) {
      return Boolean(form.name.trim() && form.addressLine.trim() && form.city.trim()) && !errorCodigoPostal(form.postalCode);
    }
    // Una superficie que no es un número no pasa: antes se enviaba como `null` en silencio.
    if (step === 1 && form.areaM2 && Number.isNaN(numeroEs(form.areaM2))) {
      return false;
    }
    if (step === 1 && errorAnio(form.builtYear)) return false;
    if (step === 2 && form.unitMode === 'entera') {
      return Boolean(form.unitRent);
    }
    // Por habitaciones: todas tienen que llevar renta. Dejar una a cero crea una habitación
    // que no se puede alquilar y que nadie recuerda haber creado así.
    if (step === 2 && form.unitMode === 'habitaciones') {
      return form.unitRents.length > 0 && form.unitRents.every((rent) => Boolean(rent));
    }
    return true;
  }, [step, form]);

  /**
   * Editando se puede guardar desde CUALQUIER paso, así que no basta con validar el que está a
   * la vista: un nombre borrado en «Identificación» tiene que bloquear el botón aunque estés en
   * «Documentos». Son las mismas dos reglas de los pasos 1 y 2; el 3 se guarda solo y el 4 no
   * tiene obligatorios.
   */
  const editValid =
    Boolean(form.name.trim() && form.addressLine.trim() && form.city.trim()) &&
    !(form.areaM2 && Number.isNaN(numeroEs(form.areaM2))) &&
    !errorCodigoPostal(form.postalCode) &&
    !errorAnio(form.builtYear);

  const markers = form.latitude !== null && form.longitude !== null
    ? [
        {
          id: 'pin',
          latitude: form.latitude,
          longitude: form.longitude,
          label: form.name || 'Ubicación del inmueble',
          approximate: form.geocodePrecision === 'municipio' || form.geocodePrecision === 'sin_geocodificar',
        },
      ]
    : [];

  const isLastStep = step === STEPS.length - 1;

  /**
   * Por qué no se puede continuar, dicho junto al botón. Un «Continuar» gris sin explicación
   * es exactamente un «no me deja»: el usuario no sabe si es un fallo o le falta algo.
   */
  const faltan: string[] = [];
  if (!editing && !stepValid) {
    if (step === 0) {
      if (!form.name.trim()) faltan.push('el nombre');
      if (!form.addressLine.trim()) faltan.push('la dirección');
      if (!form.city.trim()) faltan.push('la ciudad');
      if (errorCodigoPostal(form.postalCode)) faltan.push('un código postal válido');
    } else if (step === 1) {
      if (form.areaM2 && Number.isNaN(numeroEs(form.areaM2))) faltan.push('una superficie válida');
      if (errorAnio(form.builtYear)) faltan.push('un año de construcción válido');
    } else if (step === 2) {
      faltan.push(form.unitMode === 'entera' ? 'la renta' : 'la renta de cada habitación');
    }
  }
  const avisoFaltan =
    faltan.length === 0
      ? null
      : `Falta ${faltan.length === 1 ? faltan[0] : `${faltan.slice(0, -1).join(', ')} y ${faltan[faltan.length - 1]}`}.`;

  return (
    <Drawer
      open={open}
      title={editing ? 'Editar inmueble' : 'Nuevo inmueble'}
      subtitle={
        editing
          ? property?.name
          : 'La ocupación y los indicadores se calculan a partir de las habitaciones, no de los dormitorios.'
      }
      onClose={onClose}
      dirty={dirty}
      /**
       * `Esc` con el desplegable de direcciones abierto lo cierra a él y no el panel: si no,
       * quitar una lista de sugerencias te pregunta si quieres tirar el formulario entero.
       */
      onEscape={() => {
        if (!listOpen) return false;
        setListOpen(false);
        return true;
      }}
      footer={
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {step > 0 && (
              <Button variant="secondary" size="sm" onClick={() => setStep(step - 1)}>
                Atrás
              </Button>
            )}
            {avisoFaltan && (
              <span className={styles.faltan} role="status">
                {avisoFaltan}
              </span>
            )}
          </div>
          <div className={styles.footerRight}>
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            {/* Editando, «Siguiente» acompaña al guardado en vez de sustituirlo: cambiar el
                nombre y guardar no debería exigir recorrer los cuatro pasos. */}
            {editing && !isLastStep && (
              <Button variant="secondary" size="sm" onClick={() => setStep(step + 1)}>
                Siguiente
              </Button>
            )}
            {editing || isLastStep ? (
              <Button
                variant="primary"
                size="sm"
                disabled={save.isPending || !(editing ? editValid : stepValid)}
                onClick={() => save.mutate()}
              >
                {/*
                  Subir fotos puede tardar bastante más que crear el inmueble, así que el botón
                  dice en qué va: un «Guardando…» quieto durante veinte segundos parece colgado.
                */}
                {uploadProgress !== null
                  ? `Subiendo… ${uploadProgress}%`
                  : save.isPending
                    ? 'Guardando…'
                    : editing
                      ? 'Guardar cambios'
                      : 'Crear inmueble'}
              </Button>
            ) : (
              <Button variant="primary" size="sm" disabled={!stepValid} onClick={() => setStep(step + 1)}>
                Continuar
              </Button>
            )}
          </div>
        </div>
      }
    >
      {/* Los mismos cuatro pasos al crear y al editar. Editando se puede saltar a cualquiera. */}
      <Stepper steps={STEPS} current={step} onGoTo={setStep} free={editing} />

      {/* ------------------------------------------------ Paso 1 */}
      {step === 0 && (
        <FormSection>
          <FormRow>
            <Field label="Nombre" required error={fieldErrors.name}>
              <Input
                value={form.name}
                onChange={(event) => patch({ name: event.target.value })}
                placeholder="Calle Mayor 12"
                invalid={Boolean(fieldErrors.name)}
              />
            </Field>
            <Field label="Tipo" required>
              <Select
                options={PROPERTY_TYPES}
                value={form.propertyType}
                onChange={(event) => patch({ propertyType: event.target.value })}
              />
            </Field>
          </FormRow>

          <Field
            label="Dirección"
            required
            /**
             * `htmlFor` explícito porque el hijo de `Field` aquí es el `div` del
             * autocompletado, no el propio campo: sin esto la etiqueta quedaría apuntando al
             * contenedor y el campo, sin nombre accesible.
             */
            htmlFor="direccion"
            error={fieldErrors.addressLine}
            help={
              geocoderDegraded
                ? 'El buscador de direcciones no responde. Escribe la dirección y coloca el pin a mano en el mapa.'
                : 'Escribe y elige un resultado: se rellenan ciudad, provincia, código postal y el pin del mapa.'
            }
          >
            <div className={styles.autocomplete} ref={autocompleteRef}>
              <Input
                id="direccion"
                value={addressQuery}
                onChange={(event) => {
                  setAddressQuery(event.target.value);
                  patch({ addressLine: event.target.value });
                  // Volver a teclear descarta lo elegido: ahora sí hay algo nuevo que buscar.
                  pickedRef.current = null;
                  setListOpen(true);
                }}
                placeholder="Calle Mayor 12, Valencia"
                invalid={Boolean(fieldErrors.addressLine)}
                autoComplete="off"
                role="combobox"
                aria-expanded={listOpen && suggestions.length > 0}
                aria-controls="direccion-sugerencias"
                aria-autocomplete="list"
              />
              {searching && <span className={styles.searching}>Buscando…</span>}

              {listOpen && suggestions.length > 0 && (
                <ul className={styles.suggestions} id="direccion-sugerencias" role="listbox">
                  {suggestions.map((candidate) => (
                    <li key={candidate.id}>
                      <button type="button" role="option" onClick={() => void pickSuggestion(candidate)}>
                        <span className={styles.suggestionLabel}>{candidate.label}</span>
                        <span className={styles.suggestionMeta}>
                          {[candidate.postalCode, candidate.city, candidate.province]
                            .filter(Boolean)
                            .join(' · ')}
                          {candidate.cadastralReference && ' · con catastro'}
                          {/*
                            Se dice cuándo el resultado NO llega al portal. Es la diferencia
                            entre «esta es la casa» y «esto es el pueblo»: sin avisar, el
                            usuario elige el municipio, ve el campo relleno y da por buena una
                            dirección sin número.
                          */}
                          {candidate.precision === 'exacta'
                            ? ' · nº exacto'
                            : candidate.precision === 'calle'
                              ? ' · sin número: añádelo a mano'
                              : ' · solo el municipio'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Field>

          <FormRow>
            <Field label="Planta" optional error={fieldErrors.floor}>
              <Input
                value={form.floor}
                onChange={(event) => patch({ floor: event.target.value })}
                placeholder="3º"
                maxLength={20}
                invalid={Boolean(fieldErrors.floor)}
              />
            </Field>
            <Field label="Puerta" optional error={fieldErrors.door}>
              <Input
                value={form.door}
                onChange={(event) => patch({ door: event.target.value })}
                placeholder="B"
                maxLength={20}
                invalid={Boolean(fieldErrors.door)}
              />
            </Field>
            <Field
              label="Código postal"
              optional
              error={fieldErrors.postalCode ?? errorCodigoPostal(form.postalCode)}
            >
              <Input
                value={form.postalCode}
                onChange={(event) => patch({ postalCode: event.target.value })}
                placeholder="46001"
                inputMode="numeric"
                maxLength={5}
                invalid={Boolean(fieldErrors.postalCode ?? errorCodigoPostal(form.postalCode))}
              />
            </Field>
          </FormRow>

          <FormRow>
            <Field label="Ciudad" required error={fieldErrors.city}>
              <Input
                value={form.city}
                onChange={(event) => patch({ city: event.target.value })}
                invalid={Boolean(fieldErrors.city)}
              />
            </Field>
            <Field label="Provincia" optional error={fieldErrors.province}>
              <Input
                value={form.province}
                onChange={(event) => patch({ province: event.target.value })}
                invalid={Boolean(fieldErrors.province)}
              />
            </Field>
          </FormRow>

          <Field
            label="Referencia catastral"
            optional
            error={fieldErrors.cadastralReference}
            help="Cópiala tal cual de la escritura o del recibo del IBI."
          >
            <Input
              value={form.cadastralReference}
              onChange={(event) => patch({ cadastralReference: event.target.value.toUpperCase() })}
              placeholder="9872023VH5797S0001WX"
              invalid={Boolean(fieldErrors.cadastralReference)}
              maxLength={50}
              style={{ fontFamily: 'var(--naar-font-mono)' }}
            />
          </Field>

          <div className={styles.mapBlock}>
            <PropertyMap markers={markers} height={220} draggable onPinMove={movePin} />
            <p className={styles.mapHint}>
              <Icon name="pin" size={13} />
              {form.latitude === null ? (
                <>Sin ubicar. Elige un resultado del buscador o arrastra el pin.</>
              ) : form.geocodePrecision === 'manual' ? (
                <>Ubicación ajustada a mano.</>
              ) : form.geocodePrecision === 'exacta' ? (
                <>Ubicación exacta.</>
              ) : (
                <>Ubicación aproximada: arrastra el pin para afinarla.</>
              )}
            </p>
          </div>
        </FormSection>
      )}

      {/* ------------------------------------------------ Paso 2 */}
      {step === 1 && (
        <FormSection>
          <FormRow>
            <Field
              label="Superficie"
              optional
              error={
                fieldErrors.areaM2 ??
                (form.areaM2 && Number.isNaN(numeroEs(form.areaM2))
                  ? 'Escribe solo el número: 85 o 85,5.'
                  : undefined)
              }
            >
              <Input
                value={form.areaM2}
                onChange={(event) => patch({ areaM2: event.target.value })}
                suffix="m²"
                numeric
                inputMode="decimal"
                invalid={Boolean(fieldErrors.areaM2) || (Boolean(form.areaM2) && Number.isNaN(numeroEs(form.areaM2)))}
              />
            </Field>
            <Field label="Dormitorios" optional error={fieldErrors.bedrooms}>
              <Input
                value={form.bedrooms}
                onChange={(event) => patch({ bedrooms: event.target.value })}
                numeric
                inputMode="numeric"
                invalid={Boolean(fieldErrors.bedrooms)}
              />
            </Field>
            <Field label="Baños" optional error={fieldErrors.bathrooms}>
              <Input
                value={form.bathrooms}
                onChange={(event) => patch({ bathrooms: event.target.value })}
                numeric
                inputMode="numeric"
                invalid={Boolean(fieldErrors.bathrooms)}
              />
            </Field>
            <Field label="Año" optional error={fieldErrors.builtYear ?? errorAnio(form.builtYear)}>
              <Input
                value={form.builtYear}
                onChange={(event) => patch({ builtYear: event.target.value })}
                numeric
                inputMode="numeric"
                placeholder="1975"
                invalid={Boolean(fieldErrors.builtYear ?? errorAnio(form.builtYear))}
              />
            </Field>
          </FormRow>

          <FormRow>
            <Field label="Certificado energético" optional error={fieldErrors.energyRating}>
              <Select
                options={ENERGY_RATINGS}
                value={form.energyRating}
                onChange={(event) => patch({ energyRating: event.target.value })}
                placeholder="Sin certificado"
                invalid={Boolean(fieldErrors.energyRating)}
              />
            </Field>
            <Field
              label="Caduca el"
              optional
              error={fieldErrors.energyRatingExpiresAt}
              help="Los certificados caducan a los 10 años."
            >
              <Input
                type="date"
                value={form.energyRatingExpiresAt}
                onChange={(event) => patch({ energyRatingExpiresAt: event.target.value })}
              />
            </Field>
          </FormRow>

          <FormSection title="Adquisición">
            <FormRow>
              <Field label="Fecha" optional error={fieldErrors.acquisitionDate}>
                <Input
                  type="date"
                  value={form.acquisitionDate}
                  onChange={(event) => patch({ acquisitionDate: event.target.value })}
                />
              </Field>
              <Field label="Precio" required error={fieldErrors.acquisitionPrice}>
                <MoneyInput
                  value={form.acquisitionPrice}
                  onValueChange={(value) => patch({ acquisitionPrice: value })}
                  onChange={() => undefined}
                  invalid={Boolean(fieldErrors.acquisitionPrice)}
                />
              </Field>
            </FormRow>

            <Field
              label="Valor de mercado actual"
              optional
              error={fieldErrors.currentMarketValue}
              help={
                editing
                  ? 'Lo que vale hoy, según tu estimación o una tasación. Si lo cambias, queda registrado en el histórico de valoraciones.'
                  : 'Lo que vale hoy, según tu estimación o una tasación. Si lo dejas vacío, se toma el precio de compra.'
              }
            >
              <MoneyInput
                value={form.marketValue}
                onValueChange={(value) => patch({ marketValue: value })}
                onChange={() => undefined}
                invalid={Boolean(fieldErrors.currentMarketValue)}
              />
            </Field>

            {/*
              Costes de compra, uno por línea. Antes era un único importe, y en la ficha no
              había forma de saber qué se había metido dentro: ¿incluía la reforma? ¿y la
              comisión? Precio + estos costes es la «Inversión inmueble» de la ficha.
            */}
            <fieldset className={styles.rents}>
              <legend className={styles.rentsLegend}>
                Costes de compra
                <small>Se suman al precio para calcular la inversión del inmueble.</small>
              </legend>

              {form.costItems.length > 0 && (
                <div className={styles.costList}>
                  {form.costItems.map((row, index) =>
                    row.confirmed ? (
                      /* Confirmada: una línea fija que ya suma. Se corrige con el lápiz. */
                      <div key={index} className={styles.costLine}>
                        <span className={styles.costLineConcept}>{row.concept}</span>
                        <span className={styles.costLineAmount}>{formatEur(row.amount)}</span>
                        <button
                          type="button"
                          className={styles.costRemove}
                          aria-label={`Editar «${row.concept}»`}
                          title="Editar"
                          onClick={() => editCost(index)}
                        >
                          <Icon name="edit" size={14} />
                        </button>
                        <button
                          type="button"
                          className={`${styles.costRemove} ${styles.costRemoveDanger}`}
                          aria-label={`Quitar «${row.concept}»`}
                          title="Quitar"
                          onClick={() =>
                            patch({ costItems: form.costItems.filter((_, i) => i !== index) })
                          }
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    ) : (
                      /* En borrador: se escribe y se confirma con «+» o con Intro. Aún no suma. */
                      <div key={index} className={styles.costRow}>
                        <Input
                          value={row.concept}
                          placeholder="Concepto"
                          aria-label={`Concepto del coste ${index + 1}`}
                          maxLength={80}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            confirmCost(index);
                          }}
                          onChange={(event) =>
                            patch({
                              costItems: form.costItems.map((other, i) =>
                                i === index ? { ...other, concept: event.target.value } : other,
                              ),
                            })
                          }
                        />
                        <MoneyInput
                          ref={(element) => {
                            costAmountRefs.current[index] = element;
                          }}
                          value={row.amount}
                          aria-label={`Importe del coste ${index + 1}`}
                          onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            confirmCost(index);
                          }}
                          onValueChange={(value) =>
                            patch({
                              costItems: form.costItems.map((other, i) =>
                                i === index ? { ...other, amount: value } : other,
                              ),
                            })
                          }
                          onChange={() => undefined}
                        />
                        <button
                          type="button"
                          className={styles.costConfirm}
                          aria-label={`Añadir el coste ${row.concept || index + 1} al total`}
                          title={tieneImporte(row.amount) ? 'Añadir al total' : 'Escribe primero el importe'}
                          disabled={!tieneImporte(row.amount)}
                          onClick={() => confirmCost(index)}
                        >
                          <Icon name="plus" size={16} strokeWidth={2.4} />
                        </button>
                        <button
                          type="button"
                          className={`${styles.costRemove} ${styles.costRemoveDanger}`}
                          aria-label={`Quitar el coste ${index + 1}`}
                          title="Quitar"
                          onClick={() =>
                            patch({ costItems: form.costItems.filter((_, i) => i !== index) })
                          }
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    ),
                  )}
                </div>
              )}

              <div className={styles.costChips}>
                {COST_SUGGESTIONS.filter(
                  (concept) => !form.costItems.some((row) => row.concept.trim() === concept),
                ).map((concept) => (
                  <button
                    key={concept}
                    type="button"
                    className={styles.rentsCopy}
                    onClick={() => addCost(concept)}
                  >
                    + {concept}
                  </button>
                ))}
                <button type="button" className={styles.rentsCopy} onClick={() => addCost('')}>
                  + Otro coste
                </button>
              </div>

              {Object.entries(fieldErrors)
                .filter(([path]) => campoDe(path) === 'acquisitionCostItems')
                .slice(0, 1)
                .map(([path, message]) => (
                  <p key={path} className={styles.warning} role="alert">
                    <Icon name="info" size={15} />
                    <span>{message}</span>
                  </p>
                ))}

              <div className={styles.costTotals}>
                <span>
                  Costes{' '}
                  <strong>
                    {formatEur(
                      sumMoney(form.costItems.filter((row) => row.confirmed).map((row) => row.amount)),
                    )}
                  </strong>
                </span>
                <span>
                  Inversión total{' '}
                  <strong>
                    {formatEur(
                      sumMoney([
                        form.acquisitionPrice,
                        ...form.costItems.filter((row) => row.confirmed).map((row) => row.amount),
                      ]),
                    )}
                  </strong>
                </span>
              </div>
            </fieldset>
          </FormSection>

          {/* La financiación es opcional: un piso comprado al contado no tiene préstamo, y una
              pestaña vacía con cuatro ceros en la ficha sugiere que falta algo. */}
          <Checkbox
            label="Tiene financiación (préstamo, seguros o gastos recurrentes)"
            hint="Activa la pestaña «Financiación» en la ficha del inmueble. Puedes cambiarlo cuando quieras desde «Editar»."
            checked={form.financingEnabled}
            onChange={(event) => patch({ financingEnabled: event.target.checked })}
          />

          <Field label="Notas" optional error={fieldErrors.notes}>
            <Textarea value={form.notes} onChange={(event) => patch({ notes: event.target.value })} />
          </Field>
        </FormSection>
      )}

      {/* ------------------------------------------------ Paso 3 (edición)
          Las habitaciones ya existen, así que no hay «modo de explotación» que elegir: se
          gestionan una a una con el mismo bloque de la ficha, y cada cambio se guarda al
          momento. El plan del alta (`unitPlan`) solo sirve para crear y la API lo ignora al
          actualizar. */}
      {editing && property && step === 2 && (
        <PropertyUnits
          propertyId={property.id}
          units={property.units}
          occupancyRate={property.financials.occupancyRate}
          monthlyRent={property.financials.monthlyRent}
          canManage={canUnits}
        />
      )}

      {/* ------------------------------------------------ Paso 3 (alta) */}
      {!editing && step === 2 && (
        <FormSection
          title="Modo de explotación"
          hint="La ocupación y los indicadores financieros salen de las HABITACIONES, no de los dormitorios del inmueble. Un piso con 4 dormitorios y 0 habitaciones aparece con 0 % de ocupación."
        >
          <RadioGroup
            name="unitMode"
            legend="Cómo se alquila este inmueble"
            value={form.unitMode}
            onChange={(value) => patch({ unitMode: value as FormState['unitMode'] })}
            options={[
              {
                value: 'entera',
                title: 'Entero',
                hint: 'Se crea un solo espacio, «Completo», con la renta objetivo.',
              },
              {
                value: 'habitaciones',
                title: 'Por habitaciones',
                hint: 'Se crean n habitaciones, «Hab. 1…n», que luego se editan una a una.',
              },
              {
                value: 'ninguna',
                title: 'Todavía no lo sé',
                hint: 'Se puede añadir después, pero hasta entonces el inmueble no se puede alquilar.',
              },
            ]}
          />

          {form.unitMode === 'habitaciones' && (
            <>
              <Field
                label="Número de habitaciones"
                required
                help="Al cambiarlo aparece o desaparece una renta abajo. Lo que ya hayas escrito se conserva."
              >
                <Input
                  value={form.unitCount}
                  onChange={(event) => {
                    const texto = event.target.value;
                    const cuantas = Number(texto);
                    /*
                     * La lista solo se redimensiona con un número usable. Mientras el campo está
                     * vacío o a medias («1» camino de «12») se deja como estaba: si no, borrar
                     * para reescribir se llevaría por delante las rentas ya tecleadas.
                     */
                    patch({
                      unitCount: texto,
                      ...(Number.isInteger(cuantas) && cuantas >= 1 && cuantas <= 50
                        ? { unitRents: ajustarRentas(form.unitRents, cuantas) }
                        : {}),
                    });
                  }}
                  numeric
                  inputMode="numeric"
                  min={1}
                  max={50}
                />
              </Field>

              {/*
                Una renta POR HABITACIÓN. En un piso compartido la exterior con baño no vale lo
                mismo que la interior, y una cifra única obligaba a entrar habitación por
                habitación a corregirla justo después de crearlas.
              */}
              <fieldset className={styles.rents}>
                <legend className={styles.rentsLegend}>
                  Renta de cada habitación
                  <small>
                    Es la de referencia; el contrato de cada inquilino puede fijar otra.
                  </small>
                </legend>

                <div className={styles.rentsGrid}>
                  {form.unitRents.map((renta, indice) => (
                    <Field
                      key={indice}
                      label={`Hab. ${indice + 1}`}
                      required
                      error={fieldErrors[`unitPlan.rents.${indice}`]}
                    >
                      <MoneyInput
                        value={renta}
                        onValueChange={(value) =>
                          patch({
                            unitRents: form.unitRents.map((otra, i) =>
                              i === indice ? value : otra,
                            ),
                          })
                        }
                        onChange={() => undefined}
                      />
                    </Field>
                  ))}
                </div>

                {/* Con doce habitaciones iguales, teclear doce veces la misma cifra es trabajo
                    inventado. Solo aparece cuando hay algo que copiar y a dónde copiarlo. */}
                {form.unitRents.length > 1 && Boolean(form.unitRents[0]) && (
                  <button
                    type="button"
                    className={styles.rentsCopy}
                    onClick={() => {
                      const primera = form.unitRents[0] ?? '';
                      patch({ unitRents: form.unitRents.map(() => primera) });
                    }}
                  >
                    Poner {form.unitRents[0]} € en todas
                  </button>
                )}
              </fieldset>
            </>
          )}

          {form.unitMode === 'entera' && (
            <Field
              label="Renta objetivo"
              required
              error={fieldErrors['unitPlan.baseRent']}
              help="Es la renta de referencia; el contrato de cada inquilino puede fijar otra."
            >
              <MoneyInput
                value={form.unitRent}
                onValueChange={(value) => patch({ unitRent: value })}
                onChange={() => undefined}
              />
            </Field>
          )}

          {form.unitMode === 'ninguna' && (
            <div className={styles.warning}>
              <Icon name="info" size={15} />
              <span>
                Sin habitaciones el inmueble no se puede alquilar y aparecerá con 0 % de ocupación en
                el panel. Podrás añadirlas desde la pestaña «Habitaciones» del detalle.
              </span>
            </div>
          )}

          {form.unitMode !== 'ninguna' && (
            <Checkbox
              label="Confirmo el modo de explotación"
              hint="Cambiarlo después obliga a dar de baja las habitaciones creadas, y las que tengan contrato no se pueden borrar."
              checked
              readOnly
            />
          )}
        </FormSection>
      )}

      {/* ------------------------------------------------ Paso 4 */}
      {step === 3 && (
        <section className={`${styles.docs} ${styles.docsStep}`}>
          {/*
            Es un PASO, al crear y al editar, y se presenta como los otros tres: título, una
            línea de ayuda y el contenido. Antes, editando, iba en un desplegable cerrado al pie
            del panel, y había que saber que estaba ahí para abrirlo.
          */}
          <div className={styles.docsHead}>
            <div className={styles.docsHeadText}>
              <span className={styles.docsTitle}>Documentos del inmueble</span>
              <span className={styles.docsHint}>
                {editing
                  ? 'Arrastra cada papel a su fila, o pulsa para elegirlo. Las fotos de arriba ya están guardadas; lo nuevo se sube al guardar los cambios.'
                  : 'Arrastra cada papel a su fila, o pulsa para elegirlo. Se suben al crear el inmueble; se puede dejar para después.'}
              </span>
            </div>
            <span className={styles.docsCount}>{recuentoPendiente(pendingPhotos, pendingDocs)}</span>
          </div>

          {(
            <div
              className={arrastrando ? `${styles.docsBody} ${styles.docsBodyArrastre}` : styles.docsBody}
              onDragEnter={(event) => {
                if (traeFicheros(event)) setArrastrando(true);
              }}
              onDragOver={(event) => {
                if (traeFicheros(event)) event.preventDefault();
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setArrastrando(false);
                  setSobre(null);
                }
              }}
              onDrop={(event) => {
                // Se traga lo que caiga entre filas: si no, el navegador abre el PDF encima.
                event.preventDefault();
                setArrastrando(false);
                setSobre(null);
              }}
            >
              {/* ---- Fotos */}
              <div className={styles.docGroup}>
                <div className={styles.docGroupTitle}>Fotos</div>
                {editing && property && (
                  <PropertyPhotos propertyId={property.id} photos={property.photos} canManage />
                )}
                <div
                  className={claseFila(pendingPhotos.length > 0, arrastrando, sobre === 'fotos')}
                  onDragEnter={(event) => {
                    if (traeFicheros(event)) setSobre('fotos');
                  }}
                  onDragOver={(event) => {
                    if (!traeFicheros(event)) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setSobre((actual) => (actual === 'fotos' ? null : actual));
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSobre(null);
                    setArrastrando(false);
                    const imagenes = Array.from(event.dataTransfer.files).filter((file) =>
                      /^image\//.test(file.type),
                    );
                    if (imagenes.length === 0) return;
                    setPendingPhotos((current) => [...current, ...imagenes]);
                    setDirty(true);
                  }}
                >
                  <span className={styles.docIcon}>
                    <Icon name="image" size={17} />
                  </span>
                  <span className={styles.docMain}>
                    <span className={styles.docName}>
                      {editing ? 'Añadir más fotos' : 'Fotos del inmueble'}
                    </span>
                    <span className={styles.docMeta}>
                      {sobre === 'fotos'
                        ? 'Suelta para añadirlas'
                        : pendingPhotos.length === 0
                          ? 'Varias a la vez. La primera será la portada.'
                          : `${pendingPhotos.length} ${pendingPhotos.length === 1 ? 'foto' : 'fotos'} · la primera será la portada`}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={styles.docAction}
                    onClick={() => photoInputRef.current?.click()}
                    aria-label="Elegir fotos"
                    title="Elegir fotos"
                  >
                    <Icon name="upload" size={16} strokeWidth={2} />
                  </button>
                </div>
                {pendingPhotos.length > 0 && (
                  <ul className={styles.photoList}>
                    {pendingPhotos.map((file, index) => (
                      <li key={`${file.name}-${index}`} className={styles.photoItem}>
                        <span className={styles.photoName}>{file.name}</span>
                        <span className={styles.photoSize}>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                        <button
                          type="button"
                          className={styles.photoRemove}
                          onClick={() => setPendingPhotos((current) => current.filter((_, i) => i !== index))}
                          aria-label={`Quitar ${file.name}`}
                        >
                          <Icon name="trash" size={13} strokeWidth={2} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* ---- Grupos y subgrupos de documentos */}
              {DEED_GROUPS.map((group) => (
                <div key={group.key} className={styles.docGroup}>
                  <div className={styles.docGroupTitle}>{group.label}</div>
                  {group.kinds.map((kind) => {
                    const files = pendingDocs
                      .map((doc, index) => ({ doc, index }))
                      .filter(({ doc }) => doc.kind === kind);
                    const encima = sobre === kind;
                    return (
                      <div key={kind} className={styles.docKindBlock}>
                        <div
                          className={claseFila(files.length > 0, arrastrando, encima)}
                          onDragEnter={(event) => {
                            if (traeFicheros(event)) setSobre(kind);
                          }}
                          onDragOver={(event) => {
                            if (!traeFicheros(event)) return;
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'copy';
                          }}
                          onDragLeave={(event) => {
                            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                              setSobre((actual) => (actual === kind ? null : actual));
                            }
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setSobre(null);
                            setArrastrando(false);
                            const nuevos = Array.from(event.dataTransfer.files).filter(esDocumento);
                            if (nuevos.length === 0) {
                              toast.error('Solo PDF, JPG, PNG o WEBP.');
                              return;
                            }
                            setPendingDocs((current) => [
                              ...current,
                              ...nuevos.map((file) => ({ kind, file })),
                            ]);
                            setDirty(true);
                          }}
                        >
                          <span className={styles.docIcon}>
                            <Icon name={files.length > 0 ? 'file-lines' : 'file'} size={17} />
                          </span>
                          <span className={styles.docMain}>
                            <span className={styles.docName}>{DEED_KIND_LABELS[kind]}</span>
                            <span className={styles.docMeta}>
                              {encima
                                ? 'Suelta para adjuntarlo'
                                : files.length === 0
                                  ? 'Pendiente'
                                  : files.length === 1
                                    ? files[0]?.doc.file.name
                                    : `${files.length} ficheros`}
                            </span>
                          </span>
                          <button
                            type="button"
                            className={styles.docAction}
                            onClick={() => {
                              setDocTarget(kind);
                              docInputRef.current?.click();
                            }}
                            aria-label={`Adjuntar ${DEED_KIND_LABELS[kind]}`}
                            title="Adjuntar"
                          >
                            <Icon name="upload" size={16} strokeWidth={2} />
                          </button>
                        </div>
                        {files.length > 0 && (
                          <ul className={styles.photoList}>
                            {files.map(({ doc, index }) => (
                              <li key={`${doc.file.name}-${index}`} className={styles.photoItem}>
                                <span className={styles.photoName}>{doc.file.name}</span>
                                <span className={styles.photoSize}>
                                  {(doc.file.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                                <button
                                  type="button"
                                  className={styles.photoRemove}
                                  onClick={() =>
                                    setPendingDocs((current) => current.filter((_, i) => i !== index))
                                  }
                                  aria-label={`Quitar ${doc.file.name}`}
                                >
                                  <Icon name="trash" size={13} strokeWidth={2} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Entradas de fichero ocultas: una para fotos (varias) y otra para documentos. */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif"
            multiple
            className={styles.photoInput}
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? []);
              if (chosen.length > 0) {
                setPendingPhotos((current) => [...current, ...chosen]);
                setDirty(true);
              }
              // Se limpia para que volver a elegir el MISMO fichero dispare el evento otra vez.
              event.target.value = '';
            }}
          />
          <input
            ref={docInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            multiple
            className={styles.photoInput}
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? []);
              if (chosen.length > 0 && docTarget !== null) {
                const kind = docTarget;
                setPendingDocs((current) => [...current, ...chosen.map((file) => ({ kind, file }))]);
                setDirty(true);
              }
              setDocTarget(null);
              event.target.value = '';
            }}
          />
        </section>
      )}
    </Drawer>
  );
}

